<template>
  <div class="data-table-column-reorder" ref="reorderWrapperRef">
    <Button variant="secondary" size="sm" @click="toggleReorder">
      <Icon icon="mdi:view-column" />
      Columns
    </Button>

    <Transition name="reorder-popover">
      <div v-if="reorderOpen" class="data-table-column-reorder__popover">
        <div class="data-table-column-reorder__header">Drag to reorder • Toggle visibility</div>
        <div class="data-table-column-reorder__list" @dragover.prevent>
          <div
            v-for="(column, index) in columns"
            :key="column.key"
            :class="[
              'data-table-column-reorder__item',
              {
                'data-table-column-reorder__item--dragging': draggedIndex === index,
                'data-table-column-reorder__item--locked': index < stickyColumns,
              },
            ]"
            :draggable="index >= stickyColumns"
            @dragstart="(e) => handleDragStart(e, index)"
            @dragover.prevent="(e) => handleDragOver(e, index)"
            @dragend="handleDragEnd"
          >
            <Icon
              :icon="index < stickyColumns ? 'mdi:lock-outline' : 'mdi:drag-vertical'"
              class="data-table-column-reorder__drag-handle"
            />
            <span class="data-table-column-reorder__label">{{ column.header }}</span>
            <Toggle
              v-if="index >= stickyColumns"
              :model-value="column.visible"
              @update:model-value="(val) => handleVisibilityChange(column.key, val)"
              size="sm"
              @click.stop
            />
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted } from "vue";
import { Icon } from "@iconify/vue";
import Button from "@/components/ui/Button.vue";
import Toggle from "@/components/ui/Toggle.vue";
import type { ColumnState } from "./types";

interface Props {
  columns: ColumnState[];
  stickyColumns?: number;
  onReorder?: (columns: ColumnState[]) => void;
  onVisibilityChange?: (key: string, visible: boolean) => void;
}

const props = withDefaults(defineProps<Props>(), {
  stickyColumns: 1,
});

const reorderOpen = ref(false);
const draggedIndex = ref<number | null>(null);
const reorderWrapperRef = ref<HTMLElement | null>(null);

const toggleReorder = () => {
  reorderOpen.value = !reorderOpen.value;
};

const handleDragStart = (e: DragEvent, index: number) => {
  if (index < props.stickyColumns) {
    e.preventDefault();
    return;
  }
  draggedIndex.value = index;
  if (e.dataTransfer) {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", "");
  }
};

const handleDragOver = (e: DragEvent, index: number) => {
  e.preventDefault();
  if (draggedIndex.value === null || draggedIndex.value === index) return;
  if (index < props.stickyColumns) return;

  const newColumns = [...props.columns];
  const draggedColumn = newColumns[draggedIndex.value];
  newColumns.splice(draggedIndex.value, 1);
  newColumns.splice(index, 0, draggedColumn);

  draggedIndex.value = index;
  props.onReorder?.(newColumns);
};

const handleDragEnd = () => {
  draggedIndex.value = null;
};

const handleVisibilityChange = (key: string, visible: boolean) => {
  props.onVisibilityChange?.(key, visible);
};

const handleClickOutside = (event: MouseEvent) => {
  if (reorderWrapperRef.value && !reorderWrapperRef.value.contains(event.target as Node)) {
    reorderOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener("click", handleClickOutside);
});
</script>

<style scoped>
.data-table-column-reorder {
  position: relative;
  display: inline-flex;
}

.data-table-column-reorder__popover {
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  z-index: 50;
  width: 240px;
  padding: 8px 0 8px 8px;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.1);
}

.data-table-column-reorder__header {
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  padding: 6px 8px;
  border-bottom: 1px solid #e5e7eb;
  margin-bottom: 8px;
}

.data-table-column-reorder__list {
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-height: 350px;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 0;
  position: relative;
}

.data-table-column-reorder__list::-webkit-scrollbar {
  width: 4px;
}

.data-table-column-reorder__list::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 0 3px 3px 0;
}

.data-table-column-reorder__list::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 0 3px 3px 0;
}

.data-table-column-reorder__list::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.data-table-column-reorder__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 6px;
  cursor: grab;
  transition: background-color 0.2s ease;
  user-select: none;
  -webkit-user-select: none;
}

.data-table-column-reorder__item:active {
  cursor: grabbing;
}

.data-table-column-reorder__item:hover {
  background-color: #f3f4f6;
}

.data-table-column-reorder__item--dragging {
  background-color: #e5e7eb;
}

.data-table-column-reorder__item--locked {
  cursor: default;
  opacity: 0.6;
}

.data-table-column-reorder__item--locked:active {
  cursor: default;
}

.data-table-column-reorder__drag-handle {
  font-size: 16px;
  width: 16px;
  height: 16px;
  color: #6b7280;
  flex-shrink: 0;
}

.data-table-column-reorder__label {
  flex: 1;
  font-size: 14px;
  color: #1f2937;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reorder-popover-enter-active,
.reorder-popover-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.reorder-popover-enter-from,
.reorder-popover-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
